// Marketing utilities for UTM tracking and webhook payloads

const UTM_STORAGE_KEY = 'marketing_utm_params';
const SESSION_ID_KEY = 'marketing_session_id';

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

export interface MarketingContext {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  device: 'mobile' | 'desktop';
  is_pwa: boolean;
  session_id: string;
}

/**
 * Capture UTM params from URL and store in sessionStorage
 * Call this on page load in auth entry points
 */
export function captureUTMParams(): UTMParams {
  if (typeof window === 'undefined') return {};
  
  const params = new URLSearchParams(window.location.search);
  const utmParams: UTMParams = {};
  
  // Extract UTM params
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  utmKeys.forEach(key => {
    const value = params.get(key);
    if (value) {
      utmParams[key as keyof UTMParams] = value;
    }
  });
  
  // Only store if we have UTM params (don't overwrite existing with empty)
  if (Object.keys(utmParams).length > 0) {
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmParams));
    console.log('[Marketing] Captured UTM params:', utmParams);
  }
  
  return utmParams;
}

/**
 * Get stored UTM params from sessionStorage
 */
export function getStoredUTMParams(): UTMParams {
  if (typeof window === 'undefined') return {};
  
  try {
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

/**
 * Get or create a session ID for tracking
 */
function getSessionId(): string {
  if (typeof window === 'undefined') return '';
  
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
}

/**
 * Detect device type
 */
function getDeviceType(): 'mobile' | 'desktop' {
  if (typeof window === 'undefined') return 'desktop';
  
  const userAgent = navigator.userAgent.toLowerCase();
  const mobileKeywords = ['android', 'webos', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone'];
  const isMobile = mobileKeywords.some(keyword => userAgent.includes(keyword)) || window.innerWidth < 768;
  
  return isMobile ? 'mobile' : 'desktop';
}

/**
 * Detect if running as PWA
 */
function isPWA(): boolean {
  if (typeof window === 'undefined') return false;
  
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true ||
    document.referrer.includes('android-app://')
  );
}

/**
 * Get full marketing context for webhook payloads
 */
export function getMarketingContext(): MarketingContext {
  const utmParams = getStoredUTMParams();
  
  return {
    utm_source: utmParams.utm_source || null,
    utm_medium: utmParams.utm_medium || null,
    utm_campaign: utmParams.utm_campaign || null,
    device: getDeviceType(),
    is_pwa: isPWA(),
    session_id: getSessionId()
  };
}

/**
 * Build signup_started event payload
 */
export function buildSignupStartedPayload(data: {
  email: string;
  first_name: string;
  country: string;
}) {
  const context = getMarketingContext();
  
  return {
    event: 'signup_started',
    email: data.email,
    first_name: data.first_name,
    country: data.country,
    verified: false,
    ...context,
    app: 'buxbox',
    timestamp: new Date().toISOString()
  };
}

/**
 * Build signup_verified event payload
 */
export function buildSignupVerifiedPayload(data: {
  user_id: string;
  email: string;
  first_name: string;
  country: string;
}) {
  const context = getMarketingContext();
  
  return {
    event: 'signup_verified',
    user_id: data.user_id,
    email: data.email,
    first_name: data.first_name,
    country: data.country,
    verified: true,
    ...context,
    app: 'buxbox',
    timestamp: new Date().toISOString()
  };
}

/**
 * Build onboarding_completed event payload
 * Sends all collected onboarding preferences to Zapier
 */
export function buildOnboardingCompletedPayload(data: {
  user_id: string;
  email: string;
  first_name: string;
  last_name?: string;
  country: string;
  profile_id?: string;
  // Onboarding preferences
  reading_time: string;
  favourite_film: string;
  interests: string[];
  decade_kid: string;
  age_range: string;
  reading_goal: number;
  is_for_self: boolean | null;
  profile_name: string;
  add_child_profile: boolean;
  // Book selections (titles, not IDs)
  selected_book_titles: string[];
  more_book_titles: string[];
}) {
  const context = getMarketingContext();
  
  return {
    event: 'onboarding_completed',
    user_id: data.user_id,
    email: data.email,
    first_name: data.first_name,
    last_name: data.last_name || '',
    country: data.country,
    profile_id: data.profile_id || '',
    verified: true,
    
    // Onboarding preferences
    reading_time: data.reading_time,
    favourite_film: data.favourite_film,
    interests: data.interests,
    decade_kid: data.decade_kid,
    age_range: data.age_range,
    reading_goal: data.reading_goal,
    is_for_self: data.is_for_self,
    profile_name: data.profile_name,
    add_child_profile: data.add_child_profile,
    
    // Book selections
    selected_book_titles: data.selected_book_titles,
    more_book_titles: data.more_book_titles,
    
    ...context,
    app: 'buxbox',
    timestamp: new Date().toISOString()
  };
}

/**
 * Build subscription_started event payload
 * Sends subscription details to Zapier for welcome emails
 */
export function buildSubscriptionStartedPayload(data: {
  user_id: string;
  email: string;
  first_name: string;
  last_name?: string;
  country: string;
  // Subscription details
  subscription_tier: 'weekly' | 'monthly' | 'annual';
  subscription_status: string;
  price_paid: number;
  currency: string;
  current_period_end: string;
  stripe_subscription_id: string;
  stripe_customer_id: string;
  has_had_subscription_before: boolean;
}) {
  const context = getMarketingContext();
  
  return {
    event: 'subscription_started',
    user_id: data.user_id,
    email: data.email,
    first_name: data.first_name,
    last_name: data.last_name || '',
    country: data.country,
    verified: true,
    
    // Subscription details
    subscription_tier: data.subscription_tier,
    subscription_status: data.subscription_status,
    price_paid: data.price_paid,
    currency: data.currency,
    current_period_end: data.current_period_end,
    stripe_subscription_id: data.stripe_subscription_id,
    stripe_customer_id: data.stripe_customer_id,
    has_had_subscription_before: data.has_had_subscription_before,
    
    ...context,
    app: 'buxbox',
    timestamp: new Date().toISOString()
  };
}
